import {
  CANVAS_ACCESS_ROW_TOOLTIP,
  CANVAS_ACCESS_STATUS,
} from '../../constants/canvas-access.constants';

/**
 * @typedef {Object} CanvasAccessLocationRow
 * @property {string|number} locationKey
 * @property {string} [locationName]
 * @property {string|number|null} [canvasAccountId]
 * @property {string|number|null} [canvasRoleId]
 * @property {string} [canvasRoleLabel]
 * @property {boolean} [provisioned]
 */

/**
 * @typedef {Object} CanvasRoleOption
 * @property {string|number} id
 * @property {string} label
 */

/**
 * Returns the status chip label for a Canvas Access location row.
 * @param {CanvasAccessLocationRow} row
 * @returns {string}
 */
export const getCanvasAccessStatusLabel = (row) => {
  if (!row.canvasAccountId) {
    return CANVAS_ACCESS_STATUS.LOCATION_NOT_READY;
  }

  return row.provisioned
    ? CANVAS_ACCESS_STATUS.PROVISIONED
    : CANVAS_ACCESS_STATUS.NOT_PROVISIONED;
};

/**
 * Maps a Canvas Access status to an MUI Chip color tier for badge colouring.
 * @param {CanvasAccessLocationRow} row
 * @returns {'success'|'info'|'warning'}
 */
export const getCanvasAccessStatusTier = (row) => {
  if (!row.canvasAccountId) {
    return 'warning';
  }

  return row.provisioned ? 'success' : 'info';
};

/**
 * Returns the action-button tooltip for a Canvas Access location row.
 * @param {{ isCanvasReady: boolean, zeroRoles: boolean, provisioned: boolean }} params
 * @returns {string}
 */
export const getCanvasAccessRowActionTooltip = ({ isCanvasReady, zeroRoles, provisioned }) => {
  if (!isCanvasReady) {
    return CANVAS_ACCESS_ROW_TOOLTIP.LOCATION_NOT_READY;
  }

  if (zeroRoles) {
    return CANVAS_ACCESS_ROW_TOOLTIP.NO_ASSIGNABLE_ROLES;
  }

  return provisioned
    ? CANVAS_ACCESS_ROW_TOOLTIP.CHANGE_ROLE
    : CANVAS_ACCESS_ROW_TOOLTIP.ASSIGN_ROLE;
};

/**
 * Returns the aria-label for the row action button.
 * @param {boolean} provisioned
 * @returns {string}
 */
export const getCanvasAccessRowActionAriaLabel = (provisioned) =>
  provisioned ? CANVAS_ACCESS_ROW_TOOLTIP.CHANGE_ROLE : CANVAS_ACCESS_ROW_TOOLTIP.ASSIGN_ROLE;

/**
 * Builds the tab summary line for Canvas-ready vs total locations.
 * @param {number} canvasReadyCount
 * @param {number} totalCount
 * @param {number} provisionedCount
 * @returns {string}
 */
export const buildCanvasAccessSummary = (canvasReadyCount, totalCount, provisionedCount) =>
  `${canvasReadyCount} of ${totalCount} locations on Canvas · ${provisionedCount} staff grant active`;

/**
 * Canvas role ids are numeric in the portal API; coerce select string values when needed.
 * @param {string|number|null|undefined} canvasRoleId
 * @returns {number|null}
 */
export const normalizeCanvasRoleId = (canvasRoleId) => {
  if (canvasRoleId == null || canvasRoleId === '') {
    return null;
  }

  const numericId = Number(canvasRoleId);
  return Number.isFinite(numericId) ? numericId : null;
};

/**
 * Resolves the Canvas role label for a selected role id at a location.
 * @param {Record<string|number, CanvasRoleOption[]>} roleOptionsByLocation
 * @param {string|number} locationKey
 * @param {string|number|null|undefined} canvasRoleId
 * @returns {string|null}
 */
export const resolveCanvasRoleLabel = (roleOptionsByLocation, locationKey, canvasRoleId) => {
  if (canvasRoleId == null) {
    return null;
  }

  const roles = roleOptionsByLocation[locationKey] ?? [];
  const match = roles.find((role) => String(role.id) === String(canvasRoleId));
  return match?.label ?? null;
};

/**
 * Builds an explanatory message when Canvas Access provisioning is unavailable.
 * @param {{ status?: string, staffRole?: { roleName?: string }|null, allowedRoleNames?: string[] }|null|undefined} eligibility
 * @returns {string|null}
 */
export const buildIneligibleCanvasAccessMessage = (eligibility) => {
  if (eligibility?.status !== 'INELIGIBLE') {
    return null;
  }

  const allowedRoles = (eligibility.allowedRoleNames ?? []).join(', ') || 'none configured';

  if (!eligibility.staffRole?.roleName) {
    return `Canvas admin provisioning is not available because this staff member has no active location assignment. Allowed roles: ${allowedRoles}.`;
  }

  return `Canvas admin provisioning is not available for the ${eligibility.staffRole.roleName} role. Allowed roles: ${allowedRoles}.`;
};

/**
 * Filters locations that have a Canvas sub-account configured.
 * @param {CanvasAccessLocationRow[]} locations
 * @returns {CanvasAccessLocationRow[]}
 */
export const getCanvasReadyLocations = (locations) =>
  locations.filter((row) => row.canvasAccountId != null);

/**
 * Counts locations with an active staff Canvas grant.
 * @param {CanvasAccessLocationRow[]} canvasReadyLocations
 * @returns {number}
 */
export const getProvisionedLocationCount = (canvasReadyLocations) =>
  canvasReadyLocations.filter((row) => row.provisioned).length;

/**
 * Returns whether assignable roles have been fetched for a location.
 * @param {Record<string|number, CanvasRoleOption[]>} roleOptionsByLocation
 * @param {string|number} locationKey
 * @returns {boolean}
 */
export const hasLoadedRoleOptions = (roleOptionsByLocation, locationKey) =>
  Object.prototype.hasOwnProperty.call(roleOptionsByLocation, locationKey);

/**
 * Derives display and interaction state for a single Canvas Access row.
 * @param {CanvasAccessLocationRow} row
 * @param {{
 *   editingLocationKey: string|number|null,
 *   roleOptionsByLocation: Record<string|number, CanvasRoleOption[]>,
 *   rowActionsDisabled: boolean,
 * }} context
 * @returns {{
 *   isCanvasReady: boolean,
 *   isEditing: boolean,
 *   roles: CanvasRoleOption[],
 *   zeroRoles: boolean,
 *   disabled: boolean,
 *   statusLabel: string,
 *   statusTier: 'success'|'info'|'warning',
 *   actionTooltip: string,
 *   actionAriaLabel: string,
 *   roleLabel: string,
 * }}
 */
export const buildStaffCanvasAccessRowViewModel = (row, context) => {
  const isCanvasReady = row.canvasAccountId != null;
  const isEditing = context.editingLocationKey === row.locationKey;
  const rolesLoaded = hasLoadedRoleOptions(context.roleOptionsByLocation, row.locationKey);
  const roles = rolesLoaded ? context.roleOptionsByLocation[row.locationKey] : [];
  const zeroRoles = isCanvasReady && rolesLoaded && roles.length === 0 && !isEditing;
  const disabled = !isCanvasReady || context.rowActionsDisabled || zeroRoles;

  return {
    isCanvasReady,
    isEditing,
    roles,
    zeroRoles,
    disabled,
    statusLabel: getCanvasAccessStatusLabel(row),
    statusTier: getCanvasAccessStatusTier(row),
    actionTooltip: getCanvasAccessRowActionTooltip({
      isCanvasReady,
      zeroRoles,
      provisioned: Boolean(row.provisioned),
    }),
    actionAriaLabel: getCanvasAccessRowActionAriaLabel(Boolean(row.provisioned)),
    roleLabel: row.canvasRoleLabel || '—',
  };
};

/**
 * Returns Canvas roles common to every selected location.
 * @param {Array<string|number>} selectedLocationKeys
 * @param {Record<string|number, CanvasRoleOption[]>} roleOptionsByLocation
 * @returns {CanvasRoleOption[]}
 */
export const getCommonRoleIntersection = (selectedLocationKeys, roleOptionsByLocation) => {
  if (selectedLocationKeys.length === 0) {
    return [];
  }

  const roleLists = selectedLocationKeys
    .map((locationKey) => roleOptionsByLocation[locationKey] ?? [])
    .filter((roles) => roles.length > 0);

  if (roleLists.length === 0) {
    return [];
  }

  const [first, ...rest] = roleLists;
  return first.filter((role) => rest.every((list) => list.some((item) => item.id === role.id)));
};

/**
 * Filters location rows that are currently selected for bulk actions.
 * @param {CanvasAccessLocationRow[]} locations
 * @param {Array<string|number>} selectedLocationKeys
 * @returns {CanvasAccessLocationRow[]}
 */
export const getSelectedCanvasAccessRows = (locations, selectedLocationKeys) =>
  locations.filter((row) => selectedLocationKeys.includes(row.locationKey));

/**
 * Applies a single Canvas role to all selected location draft entries.
 * @param {Record<string|number, string|number>} draftRoleByLocation
 * @param {Array<string|number>} selectedLocationKeys
 * @param {string|number} roleId
 * @param {string} bulkSameRoleDraftKey
 * @returns {Record<string|number, string|number>}
 */
export const applyBulkSameRoleDraft = (
  draftRoleByLocation,
  selectedLocationKeys,
  roleId,
  bulkSameRoleDraftKey,
) => ({
  ...draftRoleByLocation,
  [bulkSameRoleDraftKey]: roleId,
  ...Object.fromEntries(selectedLocationKeys.map((key) => [key, roleId])),
});

/**
 * Builds provision grants from selected locations and draft role choices.
 * @param {Array<string|number>} selectedLocationKeys
 * @param {Record<string|number, string|number>} draftRoleByLocation
 * @param {Record<string|number, CanvasRoleOption[]>} roleOptionsByLocation
 * @returns {Array<{ locationKey: string|number, canvasRoleId: number, canvasRoleLabel: string }>}
 */
export const buildCanvasAccessBulkGrants = (
  selectedLocationKeys,
  draftRoleByLocation,
  roleOptionsByLocation,
) =>
  selectedLocationKeys
    .map((locationKey) => {
      const canvasRoleId = normalizeCanvasRoleId(draftRoleByLocation[locationKey]);
      if (canvasRoleId == null) {
        return null;
      }

      const canvasRoleLabel = resolveCanvasRoleLabel(roleOptionsByLocation, locationKey, canvasRoleId);
      if (!canvasRoleLabel) {
        return null;
      }

      return { locationKey, canvasRoleId, canvasRoleLabel };
    })
    .filter((grant) => grant != null);

/**
 * Builds the bulk provision panel title.
 * @param {number} count
 * @param {'same'|'individual'} mode
 * @returns {string}
 */
export const buildBulkProvisionTitle = (count, mode) => {
  const locationLabel = count === 1 ? 'location' : 'locations';
  const modeLabel = mode === 'same' ? 'same Canvas role' : 'individual roles';
  return `Provision ${count} ${locationLabel} — ${modeLabel}`;
};

/**
 * Formats selected location names for display, truncating when over maxVisible.
 * @param {CanvasAccessLocationRow[]} rows
 * @param {{ maxVisible?: number }} [options]
 * @returns {{ display: string, full: string }}
 */
export const formatSelectedLocationNames = (rows, { maxVisible = 4 } = {}) => {
  const names = rows.map((row) => row.locationName || String(row.locationKey));
  const full = names.join(', ');

  if (names.length <= maxVisible) {
    return { display: full, full };
  }

  const visible = names.slice(0, maxVisible).join(', ');
  const remaining = names.length - maxVisible;
  return {
    display: `${visible} and ${remaining} more`,
    full,
  };
};

/**
 * Returns the current role label for bulk row display, or null when not provisioned.
 * @param {CanvasAccessLocationRow} row
 * @returns {string|null}
 */
export const getBulkRowCurrentRoleLabel = (row) =>
  row.provisioned ? (row.canvasRoleLabel || '—') : null;

/**
 * Returns whether the draft role differs from the row's current provisioned role.
 * @param {CanvasAccessLocationRow} row
 * @param {string|number|null|undefined} draftRoleId
 * @returns {boolean}
 */
export const isBulkRowRoleChanged = (row, draftRoleId) => {
  if (draftRoleId == null) {
    return false;
  }

  if (!row.provisioned) {
    return true;
  }

  return String(draftRoleId) !== String(row.canvasRoleId);
};

/**
 * Returns whether every selected location has a draft role chosen.
 * @param {Array<string|number>} selectedLocationKeys
 * @param {Record<string|number, string|number|null|undefined>} draftRoleByLocation
 * @returns {boolean}
 */
export const hasAllBulkDraftRoles = (selectedLocationKeys, draftRoleByLocation) =>
  selectedLocationKeys.length > 0
  && selectedLocationKeys.every((key) => draftRoleByLocation[key] != null);

/**
 * Filters selected rows that currently have an active Canvas admin grant.
 * @param {CanvasAccessLocationRow[]} locations
 * @param {Array<string|number>} selectedLocationKeys
 * @returns {CanvasAccessLocationRow[]}
 */
export const getProvisionedSelectedRows = (locations, selectedLocationKeys) =>
  getSelectedCanvasAccessRows(locations, selectedLocationKeys).filter((row) => row.provisioned);

/**
 * Location keys to send on revoke for the given provisioned rows.
 * @param {CanvasAccessLocationRow[]} rows
 * @returns {Array<string|number>}
 */
export const buildRevokeLocationKeys = (rows) => rows.map((row) => row.locationKey);

/**
 * Builds confirmation copy for removing Canvas access at one location.
 * @param {{ staffName?: string|null, locationName?: string|null, locationKey: string|number, roleLabel?: string|null }} params
 * @returns {string}
 */
export const buildRowRemoveConfirmMessage = ({
  staffName,
  locationName,
  locationKey,
  roleLabel,
}) => {
  const who = staffName?.trim() || 'this staff member';
  const where = locationName || String(locationKey);
  const role = roleLabel || '—';
  return `Remove Canvas admin access for ${who} at ${where} (role: ${role})? This cannot be undone from here without re-provisioning.`;
};

/**
 * Builds confirmation copy for bulk removing Canvas access.
 * @param {{ staffName?: string|null, rows: CanvasAccessLocationRow[] }} params
 * @returns {string}
 */
export const buildBulkRemoveConfirmMessage = ({ staffName, rows }) => {
  const who = staffName?.trim() || 'this staff member';
  const count = rows.length;
  const locationLabel = count === 1 ? 'location' : 'locations';
  const names = formatSelectedLocationNames(rows);
  return `Remove Canvas admin access for ${who} at ${count} ${locationLabel}?\n\n${names.full}`;
};

/**
 * Formats per-location revoke failures from a portal multi-status response.
 * @param {{ failures?: Array<{ locationKey?: string|number, locationName?: string, message?: string }>, errors?: Array<{ locationKey?: string|number, locationName?: string, message?: string }> }|null|undefined} response
 * @returns {string|null}
 */
export const getRevokeFailureMessage = (response) => {
  const failures = response?.failures ?? response?.errors;
  if (!Array.isArray(failures) || failures.length === 0) {
    return null;
  }

  return failures
    .map((failure) => {
      const where = failure.locationName || failure.locationKey || 'unknown location';
      const detail = failure.message || 'Failed to remove access';
      return `${where}: ${detail}`;
    })
    .join('; ');
};
